/**
 * The cells of a condition leaf that name a path the registry does not serve — the leaf's own
 * field, or the anchor a date offset measures from.
 *
 * The condition-field catalog is derived live from organization configuration; a stored
 * condition is frozen from whatever that configuration looked like when it was written. Nothing
 * owns the invariant between the two, so a leaf can stop resolving without anyone touching the
 * rule — a sub-organization added without an incident configuration, or per-sub-organization
 * configurations that diverge, is enough.
 *
 * The rule keeps evaluating the whole time: the evaluator runs the stored `conditions_cel`
 * against real data paths and never consults the catalog. Only the editor needs the catalog, so
 * the leaf degrades to a legible read-only row here rather than falling back to a String field —
 * which handed the author a text input holding `[object Object]` and a String operator list for
 * a field nobody could resolve.
 *
 * **Legible means author-facing, not developer-facing.** The catalog supplies a field's *label*
 * and a value's *option labels*, and neither is available here — but a stored value's *shape* is
 * self-describing, and a path's last segment is a readable name on its own. So the cells below
 * phrase what they can from the leaf itself and keep the raw path visible underneath for whoever
 * is diagnosing the drift; JSON is the last resort for a shape nothing recognises, not the
 * default rendering.
 */

import React from 'react';

import Chip from '~/components/core/Atomic/Chip/Chip';
import Tooltip from '~/components/core/Atomic/Tooltip';
import { InfoIcon } from '~/components/icons';

import { operatorLabel } from './operators';
import type { HierarchicalValueShape } from './types';

/** Why the leaf is read-only, in the terms the reader needs: it is not corrupted data.
 *
 *  Says plainly that the rule cannot be saved, because that is the consequence the author meets
 *  next and the Conditions box is where they will look for the cause. The remedy names both axes
 *  the catalog is scoped by, because either can be the one that dropped the field — pointing an
 *  author whose LOB scope changed at their sub-organizations sends them to a setting that cannot
 *  help. Deleting the condition is the other way out, and it is a one-way door: the field needed
 *  to re-create it is the field that is missing.
 *
 *  Long for a hover, deliberately. The `Read-only` chip carries the at-a-glance message and the
 *  Save button states the consequence, so nobody has to read this to be unblocked — it is here
 *  for the reader who wants why. */
/** The same drift, said in one breath, for the Save button.
 *
 *  Deliberately not `UNKNOWN_FIELD_EXPLANATION`: a reader hovering a disabled button is asking
 *  why it is disabled and what to do, not what happened to a row they may not have looked at
 *  yet. The long version stays on the leaf's info icon, which is where that question is asked. */
export const UNKNOWN_FIELD_SAVE_BLOCKED =
  "A condition uses a field that is no longer available for this rule's scope. Narrow the " +
  "rule's scope, or delete that condition, to save.";

export const UNKNOWN_FIELD_EXPLANATION =
  'This condition is valid and still evaluating — the rule keeps working exactly as it is. Its ' +
  "field is no longer offered for this rule's scope, usually because the organization's " +
  'incident/LOB configuration or sub-organization set changed after the rule was written. Until ' +
  "that is resolved the rule cannot be saved at all: narrow the rule's lines of business or " +
  'sub-organizations to ones where the field is available, or delete this condition — which is ' +
  'permanent, since it cannot be re-created while the field is unavailable.';

/** The last segment of a dotted path, as a name: `claim.hierarchical.incident_details` reads
 *  `Incident Details`.
 *
 *  A guess at the label the catalog would have supplied, and it is allowed to be a guess because
 *  the exact path stays on screen directly beneath it. Two paths can humanise identically
 *  (`claim.api.incident_details` and `claim.hierarchical.incident_details` both give
 *  `Incident Details`), which is exactly why the path is shown rather than tucked into the
 *  tooltip. */
const humanizeFieldPath = (path: string): string => {
  const segment = path.split('.').pop() ?? path;
  return segment
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const isHierarchicalValue = (value: unknown): value is HierarchicalValueShape =>
  typeof value === 'object' && value !== null && 'parent' in value && 'children' in value;

const isPrimitive = (value: unknown): value is string | number | boolean =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

/** A stored value as a phrase, or `null` when nothing here recognises its shape.
 *
 *  `null` is the signal to fall back to JSON — distinct from the string `'null'`, which is what
 *  a leaf genuinely storing null renders as. */
const phraseValue = (value: unknown): string | null => {
  // A stored string is shown as it was written: `JSON.stringify('low')` is `"low"`, and the
  // quotes read as data corruption on the one row already asking not to be read that way.
  if (isPrimitive(value)) {
    return String(value);
  }
  if (value === null || value === undefined) {
    return 'null';
  }
  if (isHierarchicalValue(value)) {
    // `parent` is `string | string[]` — the compiler forbids children under multiple parents,
    // so a multi-parent value legitimately carries none.
    const parents = (Array.isArray(value.parent) ? value.parent : [value.parent]).filter(Boolean);
    const children = (value.children ?? []).filter(Boolean);
    const parentText = parents.join(', ');
    return children.length ? `${parentText} → ${children.join(', ')}` : parentText;
  }
  if (Array.isArray(value) && value.every(isPrimitive)) {
    return value.map(String).join(', ');
  }
  return null;
};

/** Caption + static text where the row's other leaves carry a labelled control, so the three
 *  columns still read as Field / Operator / Value. */
const ReadOnlyCell: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex flex-col gap-4">
    <span className="text-xs text-slate-800">{label}</span>
    {children}
  </div>
);

/** The stored path, named. `label` is the caption of whichever cell names it: the leaf's own
 *  Field, or the "Compared to" anchor of a date offset.
 *
 *  `showStatus` is off for the anchor: it sits inside the value cell of a leaf whose own Field
 *  cell already carries the chip, and one row saying "Read-only" twice reads as two problems. */
export const UnknownFieldPathCell: React.FC<{ path: string; label?: string; showStatus?: boolean }> = ({
  path,
  label = 'Field',
  showStatus = true,
}) => (
  <ReadOnlyCell label={label}>
    <span className="flex flex-col gap-4">
      <span className="flex items-center gap-8 text-sm text-slate-900">
        <span className="break-all">{humanizeFieldPath(path)}</span>
        {showStatus && (
          // The row renders no controls, and absence-of-controls is also how the builder would
          // show a disabled row — the opposite of what is true here. The chip states which one
          // this is, without requiring the tooltip to correct a first impression.
          <Chip size="small" variant="outlined" label="Read-only" />
        )}
        <Tooltip title={UNKNOWN_FIELD_EXPLANATION}>
          <span className="shrink-0 leading-none" data-testid="qb-unknown-field-info">
            <InfoIcon size={16} iconColor="#475569" />
          </span>
        </Tooltip>
      </span>
      {/* The exact stored path, for whoever is diagnosing why the field dropped out. Secondary
          to the name above it, because leading with a dotted internal path is what made this row
          read as breakage in the first place. */}
      <span className="font-mono break-all text-xs text-slate-800">{path}</span>
    </span>
  </ReadOnlyCell>
);

export const UnknownFieldOperatorCell: React.FC<{ operator: string }> = ({ operator }) => (
  <ReadOnlyCell label="Operator">
    {/* The author-facing phrase, as every other row in the builder shows it — a degraded leaf
        must not be the one place where `notNull` reads as `notNull`. */}
    <span className="break-all text-sm text-slate-900">{operatorLabel(operator)}</span>
  </ReadOnlyCell>
);

export const UnknownFieldValueCell: React.FC<{ value: unknown }> = ({ value }) => {
  const phrase = phraseValue(value);

  return (
    <ReadOnlyCell label="Value">
      {phrase === null ? (
        // Nothing recognised the shape. JSON keeps it legible rather than guessing at it, and
        // `?? null` because `JSON.stringify(undefined)` is `undefined`, not a string — though
        // `phraseValue` claims undefined before this, so this is belt and braces.
        <pre className="max-h-160 m-0 overflow-auto whitespace-pre-wrap break-all text-xs text-slate-900">
          {JSON.stringify(value ?? null, null, 2)}
        </pre>
      ) : (
        <span className="break-all text-sm text-slate-900">{phrase}</span>
      )}
    </ReadOnlyCell>
  );
};
